import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { LiveNowBanner } from "@/components/LiveNowBanner";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Heart, MessageCircle, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { MentionInput } from "@/components/MentionInput";
import { renderWithMentions } from "@/lib/mentions";

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const PostDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [busy, setBusy] = useState(false);
  const [showFeatureDialog, setShowFeatureDialog] = useState(false);
  const [editorNote, setEditorNote] = useState("");

  const load = async () => {
    const [p, c] = await Promise.all([
      api.get(`/posts/${id}`),
      api.get(`/posts/${id}/comments`),
    ]);
    setPost(p.data);
    setComments(c.data);
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const submitComment = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Please sign in to comment.");
    if (!commentText.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${id}/comments`, { text: commentText.trim() });
      setComments([...comments, data]);
      setCommentText("");
    } finally {
      setBusy(false);
    }
  };

  const toggleLike = async () => {
    if (!user) return toast.error("Please sign in to like.");
    const { data } = await api.post(`/posts/${id}/like`);
    setPost({ ...post, liked: data.liked, likes_count: data.likes_count });
  };

  const toggleFeature = async () => {
    try {
      const { data } = await api.post(`/posts/${id}/feature`, { editor_note: editorNote });
      setPost({ ...post, featured: data.featured, editor_note: data.featured ? editorNote : "" });
      setShowFeatureDialog(false);
      setEditorNote("");
      toast.success(data.featured ? "Featured in the Editor's Journal." : "Removed from the Journal.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update");
    }
  };

  const deletePost = async () => {
    if (!window.confirm("Delete this post permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/posts/${id}`);
      toast.success("Post deleted.");
      navigate("/feed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete post.");
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment? This cannot be undone.")) return;
    try {
      await api.delete(`/comments/${commentId}`);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      toast.success("Comment deleted.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete comment.");
    }
  };

  if (!post) return (
    <div className="app-shell"><Navbar /><div className="container-belle" style={{ paddingTop: 140 }}>Loading…</div></div>
  );

  const isBlog = post.post_type === "blog";

  return (
    <div data-testid="post-detail-page" className="app-shell">
      <Navbar />
      <LiveNowBanner />
      <article className="container-belle" style={{ paddingTop: 110, paddingBottom: 80, maxWidth: 880 }}>
        {/* Featured banner if this is an editor's pick */}
        {post.featured && (
          <div data-testid="featured-banner" style={{ padding: "20px 24px", background: "var(--bg-alt)", border: "1px solid var(--brand-accent)", marginBottom: 28, display: "flex", gap: 16, alignItems: "flex-start" }}>
            <Star size={20} strokeWidth={1.4} fill="var(--brand-accent)" color="var(--brand-accent)" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div className="overline" style={{ color: "var(--brand-accent)" }}>Featured in the Editor&apos;s Journal</div>
              {post.editor_note && (
                <p className="font-serif" style={{ fontSize: 19, fontStyle: "italic", lineHeight: 1.5, marginTop: 8, marginBottom: 0, color: "var(--text-primary)" }}>
                  "{post.editor_note}"
                </p>
              )}
            </div>
          </div>
        )}

        {/* Author */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
          <Link to={`/profile/${post.author?.username}`}>
            {post.author?.avatar_url ? (
              <img loading="lazy" decoding="async" src={post.author.avatar_url} alt="" style={{ width: 50, height: 50, borderRadius: "50%", objectFit: "cover" }} />
            ) : (
              <div style={{ width: 50, height: 50, borderRadius: "50%", background: "var(--bg-alt)" }} />
            )}
          </Link>
          <div>
            <Link to={`/profile/${post.author?.username}`} className="font-serif" style={{ fontSize: 20, fontWeight: 500 }}>
              {post.author?.display_name}
            </Link>
            <div className="overline" style={{ marginTop: 2 }}>{post.author?.location ? post.author.location + " · " : ""}{formatDate(post.created_at)}</div>
          </div>
        </div>

        {isBlog && post.title && (
          <>
            <div className="overline" style={{ color: "var(--brand-accent)" }}>Letter</div>
            <h1 className="editorial-h1" style={{ fontSize: "clamp(40px, 6vw, 68px)", marginTop: 12, marginBottom: 32 }}>
              {post.title}
            </h1>
          </>
        )}

        {post.media_url && (
          <div style={{ marginBottom: 36, overflow: "hidden" }}>
            {post.media_type === "video" ? (
              <video src={post.media_url} controls style={{ width: "100%" }} />
            ) : (
              <img loading="lazy" decoding="async" src={post.media_url} alt="" style={{ width: "100%", display: "block" }} />
            )}
          </div>
        )}

        {isBlog ? (
          <div className="editorial-body" style={{ fontSize: 19, color: "var(--text-primary)", whiteSpace: "pre-line", lineHeight: 1.85 }}>
            {post.body}
          </div>
        ) : (
          post.caption && (
            <p className="font-serif" style={{ fontSize: 24, lineHeight: 1.5, fontStyle: "italic", margin: 0 }}>
              {post.caption}
            </p>
          )
        )}

        {post.tags?.length > 0 && (
          <div style={{ marginTop: 24, display: "flex", flexWrap: "wrap", gap: 8 }}>
            {post.tags.map((t) => <span key={t} className="tag-pill">·{t}</span>)}
          </div>
        )}

        {/* Actions */}
        <div style={{ marginTop: 40, paddingTop: 28, borderTop: "1px solid var(--border-default)", display: "flex", alignItems: "center", gap: 28 }}>
          <button onClick={toggleLike} data-testid="detail-like-button" style={{
            background: "transparent", border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 8, padding: 0,
            color: post.liked ? "var(--brand-accent)" : "var(--text-secondary)",
            fontSize: 14, letterSpacing: "0.1em",
          }}>
            <Heart size={20} strokeWidth={1.4} fill={post.liked ? "var(--brand-accent)" : "none"} />
            {post.likes_count} likes
          </button>
          <span style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-secondary)", fontSize: 14 }}>
            <MessageCircle size={20} strokeWidth={1.4} /> {comments.length} comments
          </span>
          {user?.is_editor && (
            <button
              data-testid="feature-toggle-button"
              onClick={() => {
                if (post.featured) {
                  toggleFeature();
                } else {
                  setShowFeatureDialog(true);
                }
              }}
              style={{
                marginLeft: "auto",
                background: post.featured ? "var(--brand-accent)" : "transparent",
                color: post.featured ? "var(--text-inverse)" : "var(--brand-accent)",
                border: "1px solid var(--brand-accent)",
                padding: "8px 16px", fontSize: 11,
                letterSpacing: "0.2em", textTransform: "uppercase",
                cursor: "pointer", fontFamily: "'Outfit', sans-serif", fontWeight: 500,
                display: "flex", alignItems: "center", gap: 8,
              }}
            >
              <Star size={14} strokeWidth={1.4} fill={post.featured ? "var(--text-inverse)" : "none"} />
              {post.featured ? "Featured" : "Feature in Letter"}
            </button>
          )}
          {user && (post.author?.id === user.id || user.is_editor) && (
            <button
              data-testid="detail-delete-post-button"
              onClick={deletePost}
              title={post.author?.id === user.id ? "Delete post" : "Remove post (editor)"}
              style={{
                marginLeft: user?.is_editor ? 12 : "auto",
                background: "transparent", border: "1px solid var(--border-default)",
                color: "var(--text-muted)", padding: "8px 12px", fontSize: 11,
                letterSpacing: "0.2em", textTransform: "uppercase",
                cursor: "pointer", fontFamily: "'Outfit', sans-serif",
                display: "flex", alignItems: "center", gap: 8,
              }}
            >
              <Trash2 size={14} strokeWidth={1.4} />
              Delete
            </button>
          )}
        </div>

        {showFeatureDialog && (
          <div data-testid="feature-dialog" style={{
            marginTop: 24, padding: 28,
            background: "var(--bg-alt)", border: "1px solid var(--brand-accent)",
          }}>
            <div className="overline" style={{ color: "var(--brand-accent)" }}>Editor's note (optional)</div>
            <p className="editorial-body" style={{ fontSize: 14, marginTop: 6, marginBottom: 16 }}>
              A short line of context for the Sunday letter — why this caught your eye.
            </p>
            <textarea
              data-testid="editor-note-input"
              value={editorNote}
              onChange={(e) => setEditorNote(e.target.value)}
              placeholder="A photograph that captures the moment exactly."
              className="input-belle"
              rows={3}
              style={{ minHeight: 80, fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontStyle: "italic", borderBottom: "1px solid var(--border-dark)" }}
            />
            <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
              <button onClick={toggleFeature} data-testid="confirm-feature-button" className="btn-accent">Add to the Letter</button>
              <button onClick={() => { setShowFeatureDialog(false); setEditorNote(""); }} className="btn-secondary">Cancel</button>
            </div>
          </div>
        )}

        {/* Comments */}
        <section style={{ marginTop: 56 }}>
          <div className="overline" style={{ marginBottom: 24 }}>The conversation</div>

          {user ? (
            <form onSubmit={submitComment} style={{ display: "flex", gap: 12, marginBottom: 36, alignItems: "flex-start" }}>
              {user.avatar_url ? (
                <img loading="lazy" decoding="async" src={user.avatar_url} alt="" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} />
              ) : (
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--bg-alt)" }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <MentionInput
                  value={commentText}
                  onChange={(v) => {
                    if (v.length > 2000) {
                      setCommentText(v.slice(0, 2000));
                      toast("This comment is at its limit — send it and continue in a new one.");
                    } else {
                      setCommentText(v);
                    }
                  }}
                  placeholder="Add a quiet thought… try @ to mention someone"
                  testId="comment"
                  rows={2}
                  maxLength={2000}
                  style={{ minHeight: 60, resize: "vertical", borderBottom: "1px solid var(--border-dark)" }}
                />
                {commentText.length > 1800 && (
                  <div
                    data-testid="comment-counter"
                    style={{
                      fontSize: 11,
                      color: commentText.length >= 2000 ? "var(--brand-accent)" : "var(--text-muted)",
                      fontFamily: "'Outfit', sans-serif",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      textAlign: "right",
                      marginTop: 4,
                    }}
                  >
                    {commentText.length >= 2000
                      ? "Comment is full"
                      : `${2000 - commentText.length} characters remaining`}
                  </div>
                )}
              </div>
              <button type="submit" disabled={busy} data-testid="comment-submit-button" className="btn-primary">Post</button>
            </form>
          ) : (
            <p style={{ color: "var(--text-muted)", fontStyle: "italic" }}>
              <Link to="/login" className="link-underline" style={{ color: "var(--brand-accent)" }}>Sign in</Link> to join the conversation.
            </p>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {comments.map((c) => (
              <div key={c.id} data-testid={`comment-${c.id}`} style={{ display: "flex", gap: 14 }}>
                <Link to={`/profile/${c.author?.username}`}>
                  {c.author?.avatar_url ? (
                    <img loading="lazy" decoding="async" src={c.author.avatar_url} alt="" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--bg-alt)" }} />
                  )}
                </Link>
                <div style={{ flex: 1, paddingBottom: 16, borderBottom: "1px solid var(--border-default)" }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                    <Link to={`/profile/${c.author?.username}`} className="font-serif" style={{ fontSize: 16, fontWeight: 500 }}>
                      {c.author?.display_name}
                    </Link>
                    <span className="overline">{formatDate(c.created_at)}</span>
                    {user && (c.author?.id === user.id || post.author?.id === user.id || user.is_editor) && (
                      <button
                        data-testid={`delete-comment-${c.id}`}
                        onClick={() => deleteComment(c.id)}
                        title={
                          c.author?.id === user.id
                            ? "Delete your comment"
                            : post.author?.id === user.id
                            ? "Remove from your post"
                            : "Remove (editor)"
                        }
                        style={{
                          marginLeft: "auto", background: "transparent", border: "none",
                          cursor: "pointer", color: "var(--text-muted)", padding: 0,
                          display: "flex", alignItems: "center",
                        }}
                      >
                        <Trash2 size={14} strokeWidth={1.4} />
                      </button>
                    )}
                  </div>
                  <p style={{ marginTop: 6, marginBottom: 0, fontSize: 15, lineHeight: 1.6, color: "var(--text-primary)" }}>{renderWithMentions(c.text)}</p>
                </div>
              </div>
            ))}
            {comments.length === 0 && (
              <p style={{ color: "var(--text-muted)", fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif", fontSize: 19 }}>
                No comments yet — leave the first quiet word.
              </p>
            )}
          </div>
        </section>
      </article>
      <Footer />
    </div>
  );
};

export default PostDetail;
